"use client";
import { useState, useEffect } from "react";
import Link from "next/link";
import { saveAuth } from "@/lib/api/client";
import { authApi } from "@/lib/api/auth";
import { useI18n } from "@/i18n";

// 全球区号 —— 几内亚（+224）置顶，西非邻国排前，其他主要国家紧随
const AREA_CODES = [
    "+224", "+221", "+225", "+223", "+226", "+231", "+232", "+228", "+229", "+227",
    "+233", "+234", "+235", "+237", "+241", "+242", "+243",
    "+213", "+212", "+216", "+20",
    "+33", "+44", "+49", "+32", "+41", "+34", "+39", "+351",
    "+1", "+55", "+56",
    "+86", "+81", "+82", "+65", "+60", "+62", "+91", "+971", "+966",
    "+27", "+254", "+255", "+256", "+260", "+263",
];

export default function LoginModal({ open, onClose }) {
  const { t } = useI18n();
  const [mode, setMode] = useState("email"); // "email" | "phone"
  const [phone, setPhone] = useState("");
  const [areaCode, setAreaCode] = useState("+224");
  const [areaOpen, setAreaOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [focusField, setFocusField] = useState(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = `${scrollbarWidth}px`;
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleSwitchMode = (next) => {
    if (next === mode) return;
    setError("");
    setPassword("");
    setMode(next);
  };

  const handleEmailLogin = async () => {
    setError("");
    if (!email.trim()) { setError(t("register.validation.emailRequired")); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError(t("register.validation.emailInvalid")); return; }
    if (!password) { setError(t("register.validation.passwordRequired")); return; }
    setLoading(true);
    try {
      const res = await authApi.loginByEmail(email, password);
      if (res?.success === false) {
        setError(res.error || t("login.loginError"));
        return;
      }
      saveAuth(res?.data);
      onClose();
      window.location.reload();
    } catch (e) {
      setError(t("login.loginError"));
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneLogin = async () => {
    setError("");
    if (!phone.trim()) { setError(t("register.validation.phoneRequired")); return; }
    if (areaCode === "+224" && !/^6\d{8}$/.test(phone)) { setError(t("register.validation.phoneInvalid")); return; }
    if (!password) { setError(t("register.validation.passwordRequired")); return; }
    setLoading(true);
    try {
      const fullPhone = `${areaCode}${phone}`;
      const res = await authApi.loginByPhone(fullPhone, password);
      if (res?.success === false) {
        setError(res.error || t("login.loginError"));
        return;
      }
      saveAuth(res?.data);
      onClose();
      window.location.reload();
    } catch (e) {
      setError(t("login.loginError"));
    } finally {
      setLoading(false);
    }
  };

  const handleFacebookLogin = async () => {
    setError("");
    setLoading(true);
    try {
      const res = await authApi.getFacebookAuthUrl();
      if (res?.success === false) {
        setError(res.error || t("login.loginError"));
        return;
      }

      if (!res?.data) {
        setError(t("login.loginError"));
        return;
      }

      window.location.href = res.data;
    } catch (e) {
      setError(t("login.loginError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{modalKeyframes}</style>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(8, 15, 30, 0.55)",
          backdropFilter: "blur(10px) saturate(140%)",
          WebkitBackdropFilter: "blur(10px) saturate(140%)",
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 16,
          opacity: open ? 1 : 0,
          transition: "opacity 0.28s ease",
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "relative",
            width: "100%",
            maxWidth: 540,
            background: "#ffffff",
            borderRadius: 24,
            overflow: "hidden",
            boxShadow:
              "0 40px 100px -20px rgba(8, 15, 30, 0.45), 0 0 0 1px rgba(15, 23, 42, 0.04)",
            transform: open ? "translateY(0) scale(1)" : "translateY(20px) scale(0.97)",
            opacity: open ? 1 : 0,
            transition:
              "transform 0.42s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.28s ease",
          }}
        >
          {/* 顶部品牌色装饰条 */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              background:
                "linear-gradient(90deg, #53C906 0%, #6BD41F 35%, #F59E0B 70%, #0B66C3 100%)",
              zIndex: 3,
            }}
          />

          {/* 顶部插画区：精致渐变 + 城市 + 红绿灯 */}
          <div
            style={{
              position: "relative",
              background:
                "linear-gradient(165deg, #EEF2FF 0%, #F0F9E8 50%, #FFFBEB 100%)",
              paddingTop: 36,
              paddingBottom: 24,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              overflow: "hidden",
            }}
          >
            {/* 装饰光晕 */}
            <div
              style={{
                position: "absolute",
                top: -50,
                right: -50,
                width: 160,
                height: 160,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(83, 201, 6, 0.22) 0%, transparent 70%)",
                filter: "blur(20px)",
                pointerEvents: "none",
              }}
            />
            <div
              style={{
                position: "absolute",
                bottom: -40,
                left: -40,
                width: 140,
                height: 140,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(11, 102, 195, 0.15) 0%, transparent 70%)",
                filter: "blur(20px)",
                pointerEvents: "none",
              }}
            />
            <div
              style={{
                position: "absolute",
                top: 20,
                left: 30,
                width: 60,
                height: 60,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(245, 158, 11, 0.18) 0%, transparent 70%)",
                filter: "blur(15px)",
                pointerEvents: "none",
              }}
            />

            {/* 网格底纹 */}
            <div
              style={{
                position: "absolute",
                inset: 0,
                backgroundImage:
                  "radial-gradient(circle, rgba(15, 23, 42, 0.04) 1px, transparent 1px)",
                backgroundSize: "16px 16px",
                opacity: 0.5,
                pointerEvents: "none",
              }}
            />

            <svg
              viewBox="0 0 220 170"
              width="200"
              height="155"
              fill="none"
              style={{ position: "relative", zIndex: 1 }}
            >
              {/* 城市天际线（更精致） */}
              <g opacity="0.85">
                <rect x="10" y="110" width="16" height="32" rx="1.5" fill="#C9D0E2" />
                <rect x="10" y="110" width="16" height="3" fill="#B5BCD0" />
                <rect x="30" y="98" width="12" height="44" rx="1.5" fill="#B5BCD0" />
                <rect x="30" y="98" width="12" height="3" fill="#A1A9BF" />
                <rect x="46" y="116" width="14" height="26" rx="1.5" fill="#C9D0E2" />
                <rect x="46" y="116" width="14" height="3" fill="#B5BCD0" />
                <rect x="160" y="106" width="12" height="36" rx="1.5" fill="#B5BCD0" />
                <rect x="176" y="116" width="14" height="26" rx="1.5" fill="#C9D0E2" />
                <rect x="194" y="110" width="12" height="32" rx="1.5" fill="#B5BCD0" />
                {/* 建筑窗格 */}
                <rect x="14" y="118" width="2" height="3" fill="#E8EBF3" />
                <rect x="18" y="118" width="2" height="3" fill="#E8EBF3" />
                <rect x="22" y="118" width="2" height="3" fill="#E8EBF3" />
                <rect x="14" y="126" width="2" height="3" fill="#E8EBF3" />
                <rect x="18" y="126" width="2" height="3" fill="#E8EBF3" />
                <rect x="22" y="126" width="2" height="3" fill="#E8EBF3" />
                <rect x="34" y="108" width="2" height="3" fill="#E8EBF3" />
                <rect x="38" y="108" width="2" height="3" fill="#E8EBF3" />
                <rect x="34" y="118" width="2" height="3" fill="#E8EBF3" />
                <rect x="38" y="118" width="2" height="3" fill="#E8EBF3" />
                <rect x="164" y="114" width="2" height="3" fill="#E8EBF3" />
                <rect x="168" y="114" width="2" height="3" fill="#E8EBF3" />
                <rect x="180" y="124" width="2" height="3" fill="#E8EBF3" />
                <rect x="184" y="124" width="2" height="3" fill="#E8EBF3" />
                <rect x="198" y="118" width="2" height="3" fill="#E8EBF3" />
                <rect x="202" y="118" width="2" height="3" fill="#E8EBF3" />
              </g>

              {/* 闪烁星点（带光晕） */}
              <g>
                <circle cx="68" cy="22" r="6" fill="#0B66C3" opacity="0.15" />
                <path
                  d="M68 14 L70 20 L76 20 L71 24 L73 30 L68 26 L63 30 L65 24 L60 20 L66 20 Z"
                  fill="#0B66C3"
                />
              </g>
              <g>
                <circle cx="150" cy="30" r="5" fill="#0EA5E9" opacity="0.15" />
                <path
                  d="M150 24 L151 27 L154 27 L152 29 L153 32 L150 30 L147 32 L148 29 L146 27 L149 27 Z"
                  fill="#0EA5E9"
                />
              </g>
              <g>
                <circle cx="55" cy="58" r="5" fill="#E03131" opacity="0.15" />
                <path
                  d="M55 52 L56 55 L59 55 L57 57 L58 60 L55 58 L52 60 L53 57 L51 55 L54 55 Z"
                  fill="#E03131"
                />
              </g>

              {/* 红绿灯主体（更精致） */}
              <g>
                {/* 阴影 */}
                <ellipse cx="110" cy="138" rx="34" ry="5" fill="#0F172A" opacity="0.1" />
                {/* 杆顶 */}
                <rect x="100" y="14" width="20" height="10" rx="2" fill="#B5BCD0" />
                <rect x="100" y="14" width="20" height="3" rx="1.5" fill="#9DA4B8" />
                {/* 主体 */}
                <rect x="86" y="22" width="48" height="106" rx="9" fill="#E2E6F0" />
                <rect x="86" y="22" width="48" height="106" rx="9" fill="url(#trafficShine)" />
                <rect
                  x="86"
                  y="22"
                  width="48"
                  height="106"
                  rx="9"
                  fill="none"
                  stroke="#C9CDD9"
                  strokeWidth="1"
                />
                {/* 顶部小帽 */}
                <rect x="88" y="26" width="44" height="3" rx="1.5" fill="#C9CDD9" />
                {/* 红灯 */}
                <circle cx="110" cy="42" r="11" fill="#FA5252" opacity="0.12" />
                <circle cx="110" cy="42" r="8" fill="#FA5252" opacity="0.22" />
                <circle cx="110" cy="42" r="6" fill="#FA5252" opacity="0.5" />
                {/* 黄灯 */}
                <circle cx="110" cy="66" r="11" fill="#FAB005" opacity="0.14" />
                <circle cx="110" cy="66" r="8" fill="#FAB005" opacity="0.28" />
                <circle cx="110" cy="66" r="6" fill="#FAB005" opacity="0.6" />
                {/* 绿灯（强亮 + 光晕） */}
                <circle cx="110" cy="94" r="18" fill="#53C906" opacity="0.12" />
                <circle cx="110" cy="94" r="14" fill="#53C906" opacity="0.25" />
                <circle cx="110" cy="94" r="11" fill="#53C906" />
                <circle cx="110" cy="94" r="8" fill="#6BD41F" />
                <ellipse cx="106" cy="90" rx="3" ry="2" fill="#D5F5A3" opacity="0.9" />
                {/* 底座 */}
                <rect x="78" y="128" width="64" height="6" rx="3" fill="#C9CDD9" />
                <rect x="78" y="128" width="64" height="2" rx="1" fill="#9DA4B8" />
              </g>

              <defs>
                <linearGradient id="trafficShine" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ffffff" stopOpacity="0.7" />
                  <stop offset="40%" stopColor="#ffffff" stopOpacity="0" />
                </linearGradient>
              </defs>
            </svg>
          </div>

          {/* 关闭按钮 */}
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close")}
            style={{
              position: "absolute",
              top: 18,
              right: 18,
              width: 30,
              height: 30,
              border: "1px solid rgba(15, 23, 42, 0.08)",
              background: "rgba(255, 255, 255, 0.85)",
              backdropFilter: "blur(8px)",
              borderRadius: "50%",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--color-text-secondary)",
              zIndex: 5,
              transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#0F172A";
              e.currentTarget.style.color = "#fff";
              e.currentTarget.style.transform = "rotate(90deg) scale(1.05)";
              e.currentTarget.style.borderColor = "#0F172A";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.85)";
              e.currentTarget.style.color = "var(--color-text-secondary)";
              e.currentTarget.style.transform = "rotate(0deg) scale(1)";
              e.currentTarget.style.borderColor = "rgba(15, 23, 42, 0.08)";
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" width="14" height="14">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          {/* 主体内容 */}
          <div style={{ padding: "8px 32px 28px" }}>
            {/* 标题 + 装饰线 */}
            <div style={{ textAlign: "center", marginBottom: 18 }}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 10,
                  fontWeight: 700,
                  color: "var(--color-brand)",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  marginBottom: 6,
                }}
              >
                <span style={{ width: 16, height: 1.5, background: "var(--color-brand)" }} />
                WELCOME BACK
                <span style={{ width: 16, height: 1.5, background: "var(--color-brand)" }} />
              </div>
              <h2
                style={{
                  fontSize: 24,
                  fontWeight: 800,
                  color: "var(--color-text)",
                  margin: 0,
                  letterSpacing: "-0.02em",
                  fontFamily:
                    "'Inter', 'PingFang SC', -apple-system, sans-serif",
                }}
              >
                {t("login.titleLogin")}
              </h2>
              <p
                style={{
                  fontSize: 13,
                  color: "var(--color-text-muted)",
                  marginTop: 4,
                }}
              >
                {t("login.subtitle")}
              </p>
            </div>

            {/* 错误提示 */}
            {error && (
              <div
                style={{
                  background: "linear-gradient(135deg, rgba(239, 68, 68, 0.08), rgba(239, 68, 68, 0.04))",
                  color: "#d32f2f",
                  fontSize: 12,
                  textAlign: "center",
                  padding: "9px 12px",
                  borderRadius: 10,
                  marginBottom: 12,
                  border: "1px solid rgba(239, 68, 68, 0.18)",
                  fontWeight: 500,
                  animation: "loginShake 0.4s ease",
                }}
              >
                {error}
              </div>
            )}

            {/* 账号 + 密码快速登录 */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 12 }}>
              {mode === "email" ? (
                /* 邮箱输入 */
                <div
                  style={{
                    ...inputWrapStyle,
                    borderColor:
                      focusField === "email"
                        ? "var(--color-brand)"
                        : "var(--color-border)",
                    background:
                      focusField === "email" ? "#ffffff" : "var(--color-bg-alt, #F4F6F4)",
                    boxShadow:
                      focusField === "email"
                        ? "0 0 0 3px rgba(83, 201, 6, 0.12)"
                        : "none",
                  }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    width="16"
                    height="16"
                    style={{
                      ...inputIconStyle,
                      color:
                        focusField === "email"
                          ? "var(--color-brand)"
                          : "var(--color-text-muted)",
                    }}
                  >
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="m22 7-10 6L2 7" />
                  </svg>
                  <input
                    type="text"
                    placeholder={t("register.email")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFocusField("email")}
                    onBlur={() => setFocusField(null)}
                    onKeyDown={(e) => { if (e.key === "Enter") handleEmailLogin(); }}
                    style={inputFieldStyle}
                  />
                </div>
              ) : (
                /* 手机号输入（含区号 + 国旗） */
                <div
                  style={{
                    ...inputWrapStyle,
                    padding: "0 14px 0 8px",
                    borderColor:
                      focusField === "phone"
                        ? "var(--color-brand)"
                        : "var(--color-border)",
                    background:
                      focusField === "phone" ? "#ffffff" : "var(--color-bg-alt, #F4F6F4)",
                    boxShadow:
                      focusField === "phone"
                        ? "0 0 0 3px rgba(83, 201, 6, 0.12)"
                        : "none",
                  }}
                  onFocus={() => setFocusField("phone")}
                  onBlur={() => setFocusField(null)}
                >
                  <div
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      padding: "0 4px 0 2px",
                      borderRight: "1px solid var(--color-border-light)",
                      marginRight: 8,
                      height: 28,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setAreaOpen((v) => !v)}
                      style={{
                        border: "none",
                        background: "transparent",
                        fontSize: 13,
                        color: "var(--color-text-secondary)",
                        fontWeight: 500,
                        cursor: "pointer",
                        padding: 0,
                        height: 28,
                        display: "flex",
                        alignItems: "center",
                        gap: 3,
                        outline: "none",
                      }}
                    >
                      <span>{areaCode}</span>
                      <span style={{ fontSize: 10, color: "var(--color-text-muted)" }}>▾</span>
                    </button>
                    {areaOpen && (
                      <>
                        <div
                          style={{ position: "fixed", inset: 0, zIndex: 99 }}
                          onClick={() => setAreaOpen(false)}
                        />
                        <div
                          style={{
                            position: "absolute",
                            top: "calc(100% + 4px)",
                            left: 0,
                            zIndex: 100,
                            background: "#fff",
                            border: "1px solid var(--color-border)",
                            borderRadius: 8,
                            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                            maxHeight: 220,
                            overflowY: "auto",
                            minWidth: 180,
                            padding: 4,
                          }}
                        >
                          {AREA_CODES.map((code) => (
                            <div
                              key={code}
                              onClick={() => { setAreaCode(code); setAreaOpen(false); }}
                              style={{
                                padding: "7px 10px",
                                fontSize: 13,
                                cursor: "pointer",
                                borderRadius: 6,
                                background: code === areaCode ? "var(--color-brand-lighter, rgba(83,201,6,0.12))" : "transparent",
                                color: code === areaCode ? "var(--color-brand)" : "var(--color-text)",
                                fontWeight: code === areaCode ? 600 : 400,
                                whiteSpace: "nowrap",
                                display: "flex",
                                justifyContent: "space-between",
                                gap: 12,
                              }}
                            >
                              <span>{code}</span>
                              <span style={{ color: "var(--color-text-muted)", fontSize: 12 }}>
                                {t(`publish.countryCodes.${code}`)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, justifyContent: "center" }}>
                    <input
                      type="tel"
                      placeholder={t("register.validation.phonePlaceholder")}
                      value={phone}
                      maxLength={areaCode === "+224" ? 9 : 15}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, areaCode === "+224" ? 9 : 15))}
                      onKeyDown={(e) => { if (e.key === "Enter") handlePhoneLogin(); }}
                      style={{
                        ...inputFieldStyle,
                        padding: 0,
                      }}
                    />
                  </div>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    width="16"
                    height="16"
                    style={{
                      color: "var(--color-text-muted)",
                      flexShrink: 0,
                      marginLeft: 8,
                    }}
                  >
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                </div>
              )}

              {/* 密码输入（共用） */}
              <div
                style={{
                  ...inputWrapStyle,
                  borderColor:
                    focusField === "password"
                      ? "var(--color-brand)"
                      : "var(--color-border)",
                  background:
                    focusField === "password" ? "#ffffff" : "var(--color-bg-alt, #F4F6F4)",
                  boxShadow:
                    focusField === "password"
                      ? "0 0 0 3px rgba(83, 201, 6, 0.12)"
                      : "none",
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  width="16"
                  height="16"
                  style={{
                    ...inputIconStyle,
                    color:
                      focusField === "password"
                        ? "var(--color-brand)"
                        : "var(--color-text-muted)",
                  }}
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                <input
                  type={showPwd ? "text" : "password"}
                  placeholder={t("register.password")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setFocusField("password")}
                  onBlur={() => setFocusField(null)}
                  onKeyDown={(e) => { if (e.key === "Enter") (mode === "email" ? handleEmailLogin() : handlePhoneLogin()); }}
                  style={inputFieldStyle}
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  aria-label={showPwd ? t("common.hide") : t("common.show")}
                  style={inputTrailingStyle}
                  onMouseEnter={(e) => { e.currentTarget.style.color = "var(--color-text)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = "var(--color-text-muted)"; }}
                >
                  {showPwd ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* 记住我 + 忘记密码 */}
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 14,
            }}>
              <label style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 13,
                color: "var(--color-text-secondary)",
                cursor: "pointer",
                userSelect: "none",
              }}>
                <span
                  onClick={() => setRememberMe(!rememberMe)}
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: 4,
                    border: rememberMe
                      ? "1.5px solid var(--color-brand)"
                      : "1.5px solid var(--color-border)",
                    background: rememberMe ? "var(--color-brand)" : "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.18s ease",
                    flexShrink: 0,
                  }}
                >
                  {rememberMe && (
                    <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" width="10" height="10">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </span>
                {t("login.rememberMe")}
              </label>
              <Link
                href="/forget-password"
                onClick={onClose}
                style={{
                  fontSize: 13,
                  color: "var(--color-info, #06B6D4)",
                  textDecoration: "none",
                  fontWeight: 500,
                  transition: "opacity 0.18s ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.7"; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
              >
                {t("login.forgotPassword")}
              </Link>
            </div>

            <button
              type="button"
              onClick={mode === "email" ? handleEmailLogin : handlePhoneLogin}
              disabled={loading}
              style={{
                position: "relative",
                width: "100%",
                height: 46,
                border: "none",
                borderRadius: 12,
                background: loading
                  ? "var(--color-brand-dark)"
                  : "#53c906",
                color: "#fff",
                fontSize: 14,
                fontWeight: 700,
                letterSpacing: "0.02em",
                cursor: loading ? "wait" : "pointer",
                opacity: loading ? 0.85 : 1,
                boxShadow: "0 8px 20px -6px rgba(83, 201, 6, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
                transition: "all 0.22s cubic-bezier(0.4, 0, 0.2, 1)",
                marginBottom: 18,
                overflow: "hidden",
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = "translateY(-1.5px)";
                  e.currentTarget.style.boxShadow =
                    "0 12px 28px -6px rgba(83, 201, 6, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.2)";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 8px 20px -6px rgba(83, 201, 6, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.2)";
              }}
            >
              {/* 高光 */}
              <span
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "50%",
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0.18) 0%, transparent 100%)",
                  pointerEvents: "none",
                }}
              />
              <span style={{ position: "relative", zIndex: 1 }}>
                {loading ? t("common.loading") : t("login.loginButton")}
              </span>
            </button>

            {/* 分隔线 */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                margin: "4px 0 14px",
              }}
            >
              <div style={{ flex: 1, height: 1, background: "var(--color-border-light)" }} />
              <span
                style={{
                  fontSize: 10,
                  color: "var(--color-text-muted)",
                  letterSpacing: "0.12em",
                  fontWeight: 600,
                  textTransform: "uppercase",
                }}
              >
                {t("login.orUse")}
              </span>
              <div style={{ flex: 1, height: 1, background: "var(--color-border-light)" }} />
            </div>

            {/* 三方登录按钮（点击切换登录模式 / 触发三方登录） */}
            <div style={{ display: "flex", gap: 10, marginBottom: 18 }}>
              <button
                type="button"
                onClick={() => handleSwitchMode("phone")}
                title={t("login.phoneLogin")}
                style={{
                  ...socialBtnStyle,
                  background: mode === "phone" ? "#2F9E44" : "#ffffff",
                  color: mode === "phone" ? "#fff" : "var(--color-text-secondary)",
                  border: mode === "phone" ? "none" : "1.5px solid var(--color-border)",
                  boxShadow: mode === "phone"
                    ? "0 4px 12px -2px rgba(47, 158, 68, 0.35)"
                    : "0 1px 2px rgba(15, 23, 42, 0.04)",
                }}
                onMouseEnter={(e) => {
                  if (mode !== "phone") {
                    e.currentTarget.style.borderColor = "#2F9E44";
                    e.currentTarget.style.color = "#2F9E44";
                  }
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  if (mode !== "phone") {
                    e.currentTarget.style.borderColor = "var(--color-border)";
                    e.currentTarget.style.color = "var(--color-text-secondary)";
                  }
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="17" height="17">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                <span style={socialLabelStyle}>{t("login.phoneTab")}</span>
              </button>

              <button
                type="button"
                onClick={handleFacebookLogin}
                title={t("login.facebookLogin")}
                style={{ ...socialBtnStyle, background: "#1877F2" }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 10px 22px -4px rgba(24, 119, 242, 0.45)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 12px -2px rgba(24, 119, 242, 0.3)"; }}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17">
                  <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.989C18.343 21.128 22 16.991 22 12z" />
                </svg>
                <span style={socialLabelStyle}>Facebook</span>
              </button>

              <button
                type="button"
                onClick={() => handleSwitchMode("email")}
                title={t("login.loginWithEmail")}
                style={{
                  ...socialBtnStyle,
                  background: mode === "email" ? "#F59E0B" : "#ffffff",
                  color: mode === "email" ? "#fff" : "var(--color-text-secondary)",
                  border: mode === "email" ? "none" : "1.5px solid var(--color-border)",
                  boxShadow: mode === "email"
                    ? "0 4px 12px -2px rgba(245, 158, 11, 0.35)"
                    : "0 1px 2px rgba(15, 23, 42, 0.04)",
                }}
                onMouseEnter={(e) => {
                  if (mode !== "email") {
                    e.currentTarget.style.borderColor = "#F59E0B";
                    e.currentTarget.style.color = "#F59E0B";
                  }
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  if (mode !== "email") {
                    e.currentTarget.style.borderColor = "var(--color-border)";
                    e.currentTarget.style.color = "var(--color-text-secondary)";
                  }
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="17" height="17">
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <path d="m22 7-10 6L2 7" />
                </svg>
                <span style={socialLabelStyle}>{t("login.emailTab")}</span>
              </button>
            </div>

            {/* 底部注册链接 */}
            <div
              style={{
                textAlign: "center",
                fontSize: 13,
                color: "var(--color-text-secondary)",
                paddingTop: 16,
                borderTop: "1px solid var(--color-border-light)",
                position: "relative",
              }}
            >
              <span
                style={{
                  position: "absolute",
                  top: -8,
                  left: "50%",
                  transform: "translateX(-50%)",
                  background: "#fff",
                  padding: "0 12px",
                  fontSize: 10,
                  color: "var(--color-text-muted)",
                  letterSpacing: "0.08em",
                  fontWeight: 600,
                }}
              >
                NEW HERE?
              </span>
                {t("login.noAccount")} {" "}
              <Link
                href="/sign_up"
                onClick={onClose}
                style={{
                  color: "var(--color-brand)",
                  fontWeight: 700,
                  textDecoration: "none",
                  borderBottom: "1.5px solid transparent",
                  transition: "border-color 0.18s ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--color-brand)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "transparent"; }}
              >
                {t("login.registerNow")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const modalKeyframes = `
@keyframes loginModalIn {
  from { opacity: 0; transform: translateY(20px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes loginShake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-4px); }
  40% { transform: translateX(4px); }
  60% { transform: translateX(-2px); }
  80% { transform: translateX(2px); }
}
`;

const inputWrapStyle = {
  position: "relative",
  display: "flex",
  alignItems: "center",
  background: "var(--color-bg-alt, #F4F6F4)",
  borderRadius: 12,
  height: 46,
  padding: "0 14px",
  border: "1.5px solid var(--color-border)",
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
};

const inputIconStyle = {
  flexShrink: 0,
  marginRight: 10,
  transition: "color 0.2s ease",
};

const inputFieldStyle = {
  flex: 1,
  border: "none",
  outline: "none",
  background: "transparent",
  fontSize: 14,
  color: "var(--color-text)",
  padding: 0,
  fontFamily: "inherit",
};

const inputTrailingStyle = {
  border: "none",
  background: "transparent",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 4,
  color: "var(--color-text-muted)",
  marginLeft: 4,
  transition: "color 0.2s ease",
};

const socialBtnStyle = {
  flex: 1,
  height: 44,
  border: "none",
  borderRadius: 12,
  color: "#fff",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  boxShadow: "0 4px 12px -2px rgba(15, 23, 42, 0.12)",
  transition: "transform 0.22s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.22s ease",
  position: "relative",
  overflow: "hidden",
};

const socialLabelStyle = {
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: "0.01em",
};
